import type { QueryClient } from '@tanstack/react-query';

/**
 * Invalidate the read models that show invoice-derived TOTALS but are NOT keyed
 * by `invoiceId` — so they never refetch when a mutation only invalidates
 * `['invoice','detail', id]`.
 *
 * Client-reported (PROD-1809): after ANY invoice edit — add/edit/remove a
 * service, add/remove a discount, receive/void/refund a payment, issue/cancel —
 * the **Total Unpaid** / **Available Balance** cards stayed stale until a manual
 * page reload, on all three surfaces that host the invoice:
 *
 *   - appointment → billing  → `['appointment-details', …]`
 *       (`PreLoadAppointmentDetails.billingData.availableBalance` drives the
 *        Total Unpaid / Available Balance cards; fetched by the parent popup and
 *        passed into the embedded InvoiceDetailPage, so an `['invoice','detail']`
 *        invalidation never touches it)
 *   - Patient Billing         → `['patient-available-balance', …]` (balance card)
 *                               `['patient-invoices', …]` (invoice list + rollups)
 *   - View Claim              → shares the same invoice/balance reads
 *
 * Package entitlements ride along for the same reason (PROD-2126). Issuing an
 * invoice CONSUMES package units server-side and cancelling REVERSES that
 * consumption, but neither is keyed by `invoiceId` either:
 *
 *   - `['billing','package-grants', ...]` - the patient Packages card
 *     ("7 of 10 remaining") and the invoice "Paid by package" note. Without
 *     this, the note survived a cancel and the card showed pre-issue units
 *     until a manual reload.
 *
 * Prefix-invalidated (not exact) — React Query only refetches the currently
 * mounted instance, so the cost is one refetch on whichever surface is open.
 *
 * Call this from EVERY invoice-mutating `onSuccess`, alongside the existing
 * invoice/payments invalidations. It's additive and idempotent.
 */
export function invalidateBalanceViews(qc: QueryClient): void {
  void qc.invalidateQueries({ queryKey: ['appointment-details'] });
  void qc.invalidateQueries({ queryKey: ['patient-available-balance'] });
  void qc.invalidateQueries({ queryKey: ['patient-invoices'] });
  // Literal on purpose: use-package-grants.ts (PACKAGE_GRANTS_KEY) imports this
  // helper for useSellPackage, so importing the constant back would be a cycle.
  // The unit test pins the two in sync.
  void qc.invalidateQueries({ queryKey: ['billing', 'package-grants'] });
}
